import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  Button,
  Divider,
  IconButton,
  InputAdornment,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { Controller, useForm } from 'react-hook-form';
import z from 'zod';
import { useCheckoutWizard } from '../../hooks/useCheckoutWizard';
import { CheckoutStepIds } from '../../types';
import { ContentBox } from '@/components/shared/content-box';
import { useNotification } from '@/context/notification';
import { edupassClient } from '@/data/@clients';
import { useCatalogOfferCoupon } from '@/data/catalog/offer';
import {
  PaymentMethod,
  type OfferWithDetails,
} from '@/data/catalog/offer/interfaces';
import { formatDurationWithModality } from '@/utils/format-duration-with-modality';
import { formatMoney } from '@/utils/format-money';

const TextSkeleton = () => {
  return (
    <Skeleton
      animation="wave"
      variant="text"
      width="100%"
      height={24}
      sx={{ minWidth: 100 }}
    />
  );
};

interface DetailsCardProps {
  offer: OfferWithDetails;
  onContinue: () => Promise<void>;
  isLoading: boolean;
}

const couponSchema = z.object({
  coupon: z.string().min(1, 'Campo obrigatório'),
});

type CouponFormData = z.infer<typeof couponSchema>;

export const DetailsCard = ({
  offer,
  onContinue,
  isLoading,
}: DetailsCardProps) => {
  const {
    currentStepId,
    paymentMethod,
    creditCardData,
    actions,
    coupon: checkoutCoupon,
  } = useCheckoutWizard();
  const { coupon, isPending } = useCatalogOfferCoupon(Number(offer?.id));
  const { toast } = useNotification();
  const [appliedCoupon, setAppliedCoupon] = useState<string | null>(null);
  const [isRemovingCoupon, setIsRemovingCoupon] = useState(false);

  const {
    control,
    formState,
    setError,
    clearErrors,
    handleSubmit: handleCouponSubmit,
  } = useForm<CouponFormData>({
    resolver: zodResolver(couponSchema),
    defaultValues: {
      coupon: '',
    },
  });

  const cardNumber = creditCardData?.number;
  const showCardNumber = cardNumber && paymentMethod === PaymentMethod.Card;
  const shouldShowCoupon = currentStepId === CheckoutStepIds.CustomerInfo;

  const handleApplyCoupon = async (data: CouponFormData) => {
    coupon(
      { coupon: data.coupon },
      {
        onSuccess: (response) => {
          actions.updateValues({ offer: response.data.offer });
          actions.updateValues({
            coupon: {
              code: data?.coupon?.toUpperCase(),
              discount: response?.data?.coupon?.discount,
            },
          });
          setAppliedCoupon(data.coupon.toUpperCase());
          clearErrors();
        },
        onError: () => {
          setError('coupon', { message: 'Cupom inválido' });
        },
      }
    );
  };

  const handleRemoveCoupon = async () => {
    if (!offer?.id) return;

    setIsRemovingCoupon(true);
    try {
      const response = await edupassClient.get<{ data: OfferWithDetails }>(
        `/catalog/offer/${offer.id}`
      );
      actions.updateValues({ offer: response.data });
      actions.updateValues({ coupon: null });
      setAppliedCoupon(null);
      clearErrors();
    } catch {
      toast({
        content: 'Erro ao remover cupom.',
        type: 'error',
      });
    } finally {
      setIsRemovingCoupon(false);
    }
  };

  return (
    <ContentBox
      mt={3}
      spacing={3}
      width="100%"
      maxWidth={{ xs: '100%', md: 384 }}
    >
      <Typography variant="h2">Resumo da compra</Typography>
      <Divider />
      <Stack spacing={1}>
        <Typography>{offer?.course?.name || <TextSkeleton />}</Typography>
        <Typography>
          {(offer?.course?.course_type?.label &&
            `${offer?.course?.course_type?.label} • `) || <TextSkeleton />}
          {formatDurationWithModality(
            offer?.course?.duration?.quantity,
            offer?.course?.duration.type,
            offer?.course?.modality?.type
          )}
        </Typography>
      </Stack>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="body2">Subtotal</Typography>
          <Typography variant="body2">
            {offer?.checkout_pricing?.original_amount ? (
              formatMoney(offer?.checkout_pricing?.original_amount)
            ) : (
              <TextSkeleton />
            )}
          </Typography>
        </Stack>
        {appliedCoupon && (
          <Stack direction="row" justifyContent="space-between" spacing={1}>
            <Typography variant="body2" color="success.dark">
              {appliedCoupon}{' '}
              {checkoutCoupon?.discount && `(${checkoutCoupon.discount}%)`}
            </Typography>
            <Typography variant="body2" color="success.dark">
              -
              {offer?.checkout_pricing?.coupon_discount_amount &&
                formatMoney(offer?.checkout_pricing?.coupon_discount_amount)}
            </Typography>
          </Stack>
        )}
      </Stack>
      <Divider />
      <Stack direction="row" justifyContent="space-between">
        <Typography variant="h3">Total</Typography>
        <Stack alignItems="end">
          <Typography variant="h3">
            {offer?.checkout_pricing?.calculated_amount ? (
              formatMoney(offer?.checkout_pricing?.calculated_amount)
            ) : (
              <TextSkeleton />
            )}
          </Typography>
          {showCardNumber && (
            <Typography mt={1}>
              Cartão de crédito **** {cardNumber?.slice(-4)}
            </Typography>
          )}
        </Stack>
      </Stack>
      {shouldShowCoupon && appliedCoupon && (
        <Stack
          px={2}
          py={1.2}
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          bgcolor="success.light"
          borderRadius={({ shape }) => `${shape.borderRadius}px`}
        >
          <Typography color="success.dark" variant="h3">
            {appliedCoupon}
          </Typography>
          <IconButton
            onClick={handleRemoveCoupon}
            disabled={isRemovingCoupon}
            loading={isRemovingCoupon}
          >
            <DeleteOutlineIcon sx={{ color: 'success.dark' }} />
          </IconButton>
        </Stack>
      )}
      {shouldShowCoupon && !appliedCoupon && (
        <Stack direction="row" alignItems="start" spacing={1}>
          <Controller
            name="coupon"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                placeholder="Cupom"
                sx={{ flex: 1 }}
                error={!!formState.errors.coupon}
                helperText={formState.errors.coupon?.message}
                slotProps={{
                  input: {
                    endAdornment: formState.errors.coupon ? (
                      <InputAdornment position="end">
                        <ErrorOutlineIcon color="error" />
                      </InputAdornment>
                    ) : null,
                  },
                }}
              />
            )}
          />
          <Button
            onClick={() =>
              appliedCoupon
                ? handleRemoveCoupon()
                : handleCouponSubmit(handleApplyCoupon)()
            }
            loading={isPending || isRemovingCoupon}
          >
            {appliedCoupon ? 'Remover' : 'Aplicar'}
          </Button>
        </Stack>
      )}
      <Button
        variant="contained"
        fullWidth
        onClick={onContinue}
        loading={isLoading}
      >
        {currentStepId === CheckoutStepIds.DataConfirmation
          ? 'Confirmar compra'
          : 'Continuar'}
      </Button>
    </ContentBox>
  );
};
